import { CONSENT_STATE_SCHEMA_VERSION } from './constants.js';
import { hasOwn, isConsentState, isRecord } from './validation.js';
import type { ConsentState } from './types.js';

export const MAX_CONSENT_COOKIE_VALUE_LENGTH = 2048;

interface ConsentCookiePayloadV1 {
  readonly id: string;
  readonly sv: 1;
  readonly r: number;
  readonly pv: string;
  readonly cv: number;
  readonly p: 0 | 1;
  readonly a: 0 | 1;
  readonly m: 0 | 1;
  readonly u: number;
}

export type ConsentCookieDecodeFailureReason =
  | 'missing'
  | 'malformed'
  | 'unsupported_schema_version'
  | 'invalid_state';

export type ConsentCookieDecodeResult =
  | { readonly ok: true; readonly state: ConsentState }
  | {
      readonly ok: false;
      readonly reason: ConsentCookieDecodeFailureReason;
    };

export class ConsentCookieCodecError extends Error {
  readonly code: 'invalid_state' | 'value_too_large';

  constructor(
    code: 'invalid_state' | 'value_too_large',
    message: string,
  ) {
    super(message);
    this.name = 'ConsentCookieCodecError';
    this.code = code;
  }
}

function booleanToBit(value: boolean): 0 | 1 {
  return value ? 1 : 0;
}

function isBit(value: unknown): value is 0 | 1 {
  return value === 0 || value === 1;
}

function isPayloadV1(value: unknown): value is ConsentCookiePayloadV1 {
  if (!isRecord(value)) {
    return false;
  }

  const expectedKeys = ['id', 'sv', 'r', 'pv', 'cv', 'p', 'a', 'm', 'u'];

  return (
    Object.keys(value).length === expectedKeys.length &&
    expectedKeys.every((key) => hasOwn(value, key)) &&
    value.sv === CONSENT_STATE_SCHEMA_VERSION &&
    typeof value.id === 'string' &&
    Number.isSafeInteger(value.r) &&
    (value.r as number) >= 1 &&
    typeof value.pv === 'string' &&
    Number.isSafeInteger(value.cv) &&
    (value.cv as number) >= 1 &&
    isBit(value.p) &&
    isBit(value.a) &&
    isBit(value.m) &&
    Number.isSafeInteger(value.u) &&
    (value.u as number) >= 0
  );
}

function payloadToState(payload: ConsentCookiePayloadV1): ConsentState | null {
  const updatedAt = new Date(payload.u * 1000)
    .toISOString()
    .replace('.000Z', 'Z');
  const state = {
    id: payload.id,
    schemaVersion: payload.sv,
    revision: payload.r,
    policyVersion: payload.pv,
    configurationVersion: payload.cv,
    categories: {
      necessary: true,
      preferences: payload.p === 1,
      analytics: payload.a === 1,
      marketing: payload.m === 1,
    },
    updatedAt,
  };

  if (!isConsentState(state)) {
    return null;
  }

  Object.freeze(state.categories);
  return Object.freeze(state);
}

export function encodeConsentCookie(state: ConsentState): string {
  if (!isConsentState(state)) {
    throw new ConsentCookieCodecError(
      'invalid_state',
      'Only a valid current-schema consent state can be encoded.',
    );
  }

  const payload: ConsentCookiePayloadV1 = {
    id: state.id,
    sv: 1,
    r: state.revision,
    pv: state.policyVersion,
    cv: state.configurationVersion,
    p: booleanToBit(state.categories.preferences),
    a: booleanToBit(state.categories.analytics),
    m: booleanToBit(state.categories.marketing),
    u: Math.floor(Date.parse(state.updatedAt) / 1000),
  };
  const encoded = encodeURIComponent(JSON.stringify(payload));

  if (encoded.length > MAX_CONSENT_COOKIE_VALUE_LENGTH) {
    throw new ConsentCookieCodecError(
      'value_too_large',
      'Encoded consent cookie exceeds the supported size limit.',
    );
  }

  return encoded;
}

export function decodeConsentCookie(
  encoded: string | null | undefined,
): ConsentCookieDecodeResult {
  if (encoded === null || encoded === undefined || encoded.length === 0) {
    return { ok: false, reason: 'missing' };
  }

  if (encoded.length > MAX_CONSENT_COOKIE_VALUE_LENGTH) {
    return { ok: false, reason: 'malformed' };
  }

  let parsed: unknown;

  try {
    parsed = JSON.parse(decodeURIComponent(encoded));
  } catch {
    return { ok: false, reason: 'malformed' };
  }

  if (!isRecord(parsed)) {
    return { ok: false, reason: 'malformed' };
  }

  if (
    hasOwn(parsed, 'sv') &&
    parsed.sv !== CONSENT_STATE_SCHEMA_VERSION
  ) {
    return { ok: false, reason: 'unsupported_schema_version' };
  }

  if (!isPayloadV1(parsed)) {
    return { ok: false, reason: 'malformed' };
  }

  try {
    const state = payloadToState(parsed);
    return state
      ? { ok: true, state }
      : { ok: false, reason: 'invalid_state' };
  } catch {
    return { ok: false, reason: 'invalid_state' };
  }
}
